import { Link, useParams } from "react-router-dom";
import { articles, categories } from "@/mocks/content";
import ArticleCard from "@/components/feature/ArticleCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import ArticleBody, { buildToc, indexBlocks } from "./components/ArticleBody";

export default function ArticlePage() {
  const { id = "" } = useParams();
  const article = articles.find((a) => String(a.id) === id);

  const catName = article
    ? categories.find((c) => c.slug === article.categorySlug)?.name ?? "话题"
    : "";

  const related = article
    ? [
        ...articles.filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id),
        ...articles.filter((a) => a.categorySlug !== article.categorySlug && a.id !== article.id),
      ].slice(0, 3)
    : [];

  useDocumentMeta({
    title: article ? `${article.title} - 看料91` : "内容不存在 - 看料91",
    description: article
      ? `${article.excerpt} 看料91 整理相关内容，支持翻看、阅读与查看完整现场记录。`
      : "该条内容暂不存在，返回看料91首页浏览其他热门爆料与现场记录。",
    keywords: article ? `看料91,${article.tags.join(",")},${catName}` : "看料91",
    path: `/article/${id}`,
    jsonLd: article
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              headline: article.title,
              description: article.excerpt,
              image: article.cover,
              datePublished: article.date,
              dateModified: article.date,
              inLanguage: "zh-CN",
              author: { "@type": "Organization", name: article.author },
              publisher: { "@type": "Organization", name: "看料91" },
              articleSection: catName,
              url: `https://fxwlkj.cn/article/${article.id}`,
              mainEntityOfPage: `https://fxwlkj.cn/article/${article.id}`,
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://fxwlkj.cn/" },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: catName,
                  item: `https://fxwlkj.cn/category/${article.categorySlug}`,
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: article.title,
                  item: `https://fxwlkj.cn/article/${article.id}`,
                },
              ],
            },
          ],
        }
      : undefined,
  });

  if (!article) {
    return (
      <div className="w-full px-4 md:px-8 pt-32 pb-24 text-center">
        <span className="w-14 h-14 mx-auto flex items-center justify-center text-foreground-300">
          <i className="ri-file-unknow-line text-5xl" />
        </span>
        <h1 className="font-heading text-2xl font-black text-foreground-950 mt-4">这条内容找不到了</h1>
        <p className="text-sm text-foreground-600 mt-3">它可能已被整理或移动，不妨看看其他热门内容。</p>
        <Link
          to="/"
          className="inline-block mt-6 px-5 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm hover:bg-primary-600 transition-colors cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const items = indexBlocks(article.content);
  const toc = buildToc(article.content);

  return (
    <>
      <Breadcrumb
        items={[
          { label: catName, to: `/category/${article.categorySlug}` },
          { label: article.title },
        ]}
      />

      <article className="w-full px-4 md:px-8">
        <header className="max-w-3xl">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Link
              to={`/category/${article.categorySlug}`}
              className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 hover:bg-secondary-200 cursor-pointer"
            >
              {catName}
            </Link>
            {article.hot && (
              <span className="text-xs px-3 py-1 rounded-full bg-primary-100 text-primary-800 flex items-center gap-1">
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-fire-fill text-xs" />
                </span>
                热料
              </span>
            )}
          </div>
          <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-snug">
            {article.title}
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mt-4 leading-relaxed">
            {article.excerpt}
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-5 text-xs md:text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-user-3-line text-base" />
              </span>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line text-base" />
              </span>
              {article.date}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-eye-line text-base" />
              </span>
              {article.views.toLocaleString("zh-CN")} 次浏览
            </span>
          </div>
        </header>

        <div className="w-full aspect-[16/9] md:aspect-[21/9] rounded-lg overflow-hidden bg-background-100 mt-8">
          <img
            src={article.cover}
            alt={`${article.title} - ${catName}现场记录配图`}
            title={`${article.title} 看料91`}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-12">
          <div className="lg:col-span-8">
            <ArticleBody items={items} />

            <div className="flex flex-wrap items-center gap-2 mt-10 pt-6 border-t border-background-200">
              <span className="text-sm text-foreground-500 flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-price-tag-3-line text-base" />
                </span>
                相关标签
              </span>
              {article.tags.map((t) => (
                <span
                  key={t}
                  className="text-xs px-3 py-1 rounded-full bg-background-100 text-foreground-700"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 flex flex-col gap-6">
              {toc.length > 0 && (
                <nav className="bg-background-100 rounded-lg p-5" aria-label="内容目录">
                  <h2 className="font-heading text-base font-bold text-foreground-950 mb-3 flex items-center gap-2">
                    <span className="w-4 h-4 flex items-center justify-center text-primary-600">
                      <i className="ri-list-check-2 text-base" />
                    </span>
                    内容目录
                  </h2>
                  <ul className="space-y-2.5">
                    {toc.map((t) => (
                      <li key={t.id}>
                        <a
                          href={`#${t.id}`}
                          className="block text-sm text-foreground-600 hover:text-primary-700 cursor-pointer leading-relaxed"
                        >
                          {t.text}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}

              <div className="bg-secondary-900 rounded-lg p-5">
                <h2 className="font-heading text-base font-bold text-white mb-2">继续翻看</h2>
                <p className="text-sm text-secondary-100/80 leading-relaxed mb-4">
                  想看更多同主题内容？进入分类页，按最新或最热排序，一次看个够。
                </p>
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="inline-block px-4 py-2.5 bg-accent-500 text-secondary-950 rounded-md text-sm hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
                >
                  浏览 {catName}
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </article>

      <section className="w-full px-4 md:px-8 py-14 md:py-20">
        <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950 mb-8">
          相关推荐
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {related.map((a) => (
            <ArticleCard key={a.id} article={a} />
          ))}
        </div>
      </section>
    </>
  );
}